/**
 * Shop by Team — league strip + league/team picker.
 *
 * The picker extends ModalDialog (assets/global.js), inheriting backdrop click,
 * Escape, bodyScroll lock/unlock and trapFocus. global.js is loaded `defer` in
 * <head>, so it has executed by the time this file runs.
 *
 * Layout is CSS-only: the same element is a bottom sheet under 750px and a
 * centred modal above it (assets/section-shop-by-team.css). Nothing here needs
 * to know which one is on screen.
 */

class ShopByTeamModal extends ModalDialog {
  constructor() {
    super();

    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
    this.panelsHolder = this.querySelector(
      '.shop-by-team-modal__panels'
    );

    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.selectTab(index));
      tab.addEventListener('keydown', event =>
        this.onTabKeydown(event, index)
      );
    });
  }

  connectedCallback() {
    // ModalDialog relocates itself to <body> on first connect. In the theme
    // editor a section re-render replaces the section but leaves the relocated
    // copy behind, so drop any earlier element carrying this id before moving
    // in. Iterating the tag rather than calling getElementById on purpose:
    // getElementById returns the first match in document order, which at this
    // point is this element inside the freshly rendered section, not the stale
    // copy parked at the end of <body>.
    if (!this.moved) {
      document
        .querySelectorAll('shop-by-team-modal')
        .forEach(modal => {
          if (modal !== this && modal.id === this.id) modal.remove();
        });
    }

    super.connectedCallback();
  }

  show(opener, index = 0) {
    super.show(opener);
    this.selectTab(index);
  }

  selectTab(index) {
    if (index < 0 || index >= this.tabs.length) return;

    this.tabs.forEach((tab, i) => {
      const isSelected = i === index;

      tab.setAttribute('aria-selected', isSelected);
      tab.setAttribute('tabindex', isSelected ? '0' : '-1');
    });

    this.panels.forEach((panel, i) => {
      panel.toggleAttribute('hidden', i !== index);
    });

    // A tall grid left scrolled from the previous league reads as a broken
    // panel when the next one opens part-way down.
    if (this.panelsHolder) this.panelsHolder.scrollTop = 0;

    this.selectedIndex = index;
  }

  onTabKeydown(event, index) {
    const lastIndex = this.tabs.length - 1;
    let nextIndex = null;

    switch (event.key) {
      case 'ArrowRight':
        nextIndex = index === lastIndex ? 0 : index + 1;
        break;
      case 'ArrowLeft':
        nextIndex = index === 0 ? lastIndex : index - 1;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = lastIndex;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.selectTab(nextIndex);
    this.tabs[nextIndex].focus();
  }
}

customElements.define('shop-by-team-modal', ShopByTeamModal);

class ShopByTeam extends HTMLElement {
  constructor() {
    super();

    this.modalId = this.dataset.modal;

    this.querySelectorAll('[data-league-index]').forEach(link => {
      link.addEventListener('click', this.onOpenerClick.bind(this));
    });
  }

  onOpenerClick(event) {
    // The openers are real links to each league collection so the strip still
    // works before this script runs, and with JS off. Only take over once the
    // picker is actually on the page.
    const modal = document.getElementById(this.modalId);

    if (!modal || typeof modal.show !== 'function') return;

    event.preventDefault();

    const link = event.currentTarget;
    const index = parseInt(link.dataset.leagueIndex, 10) || 0;

    modal.show(link, index);
  }
}

customElements.define('shop-by-team', ShopByTeam);
